import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Card, Typography, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, CircularProgress, Chip, Stack, Alert,
  Snackbar, useMediaQuery, useTheme, Drawer, FormControl, InputLabel,
  Select, MenuItem, SelectChangeEvent, Checkbox, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import RefreshIcon from '@mui/icons-material/Refresh';
import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';
import { adminRefreshToken } from '../../features/admin/adminApi.ts';

import {
  useListRoleMappingsQuery,
  useCreateRoleMappingMutation,
  useUpdateRoleMappingMutation,
  useDeleteRoleMappingMutation,
  RoleMapping,
  Permission,
  MENU_ITEMS,
} from '../../features/admin/roleMapping.ts';
import { useListRolesQuery } from '../../features/admin/roleMasterApi.ts';

const BASE_URL = process.env.REACT_APP_SERVER_ENDPOINT;
const AUTH_KEY = '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3';

// ── Extended Permission type with granular access ──────────────────────────
interface ExtendedPermission {
  menu_id: string;
  menu_name: string;
  can_read: boolean;
  can_write: boolean;   // add + edit
  can_delete: boolean;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

const formatDate = (value: any): string => {
  if (!value) return 'N/A';
  if (typeof value === 'number') {
    const date = new Date(value * 1000);
    if (!isNaN(date.getTime())) return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }
  if (typeof value === 'string') {
    const date = new Date(value);
    if (!isNaN(date.getTime())) return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    return value;
  }
  return 'N/A';
};

const RoleMappingManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState<RoleMapping | null>(null);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  const [formData, setFormData] = useState({
    role_id: '',
    permissions: [] as ExtendedPermission[],
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'success' });
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [authError, setAuthError] = useState('');
  const [isManualRefresh, setIsManualRefresh] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const role = (localStorage.getItem('user_role') || localStorage.getItem('admin_type') || '').toLowerCase().trim();
    const isSuper = role.includes('super') || role === 'super_admin' || role === 'superadmin';
    setIsSuperAdmin(isSuper);
    if (!token) setAuthError('Authentication token missing. Please login again.');
    else if (!isSuper) setAuthError('Only Super Admin can access this page.');
    else setAuthError('');
  }, []);

  const {
    data: mappingsData,
    isLoading: isLoadingMappings,
    refetch: refetchMappings,
    error: mappingsError,
    isError: isMappingsError,
  } = useListRoleMappingsQuery(undefined, { skip: !isSuperAdmin });

  const { data: rolesData, isLoading: isLoadingRoles, refetch: refetchRoles } =
    useListRolesQuery(undefined, { skip: !isSuperAdmin });

  const [createMapping, { isLoading: isCreating }] = useCreateRoleMappingMutation();
  const [updateMapping, { isLoading: isUpdating }] = useUpdateRoleMappingMutation();
  const [deleteMapping, { isLoading: isDeleting }] = useDeleteRoleMappingMutation();

  const handleManualRefresh = async () => {
    setIsManualRefresh(true);
    try {
      const token = await adminRefreshToken();
      if (!token) {
        setSnackbar({ open: true, message: 'Unable to refresh authentication.', severity: 'error' });
        return;
      }
      await Promise.all([refetchMappings(), refetchRoles()]);
      setSnackbar({ open: true, message: 'Data refreshed successfully', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to refresh data', severity: 'error' });
    } finally {
      setIsManualRefresh(false);
    }
  };

  // ── Parse rights string into ExtendedPermission flags ─────────────────────
  const parseRightsToPermission = (menuId: string, menuName: string, rightsStr: string): ExtendedPermission => {
    const rights = rightsStr.split(',').map((r) => r.trim());
    return {
      menu_id: menuId,
      menu_name: menuName,
      can_read: rights.includes('view'),
      can_write: rights.includes('add') || rights.includes('edit'),
      can_delete: rights.includes('delete'),
    };
  };

  // ── Build rights string from ExtendedPermission flags ─────────────────────
  const permissionToRightsString = (perm: ExtendedPermission): string => {
    const rights: string[] = [];
    if (perm.can_read) rights.push('view');
    if (perm.can_write) rights.push('add', 'edit');
    if (perm.can_delete) rights.push('delete');
    return rights.join(',');
  };

  const fetchRoleDetailDirectly = useCallback(async (roleId: number, mapping: RoleMapping) => {
    setPermissionsLoading(true);
    setPermissionsLoaded(false);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BASE_URL}/admin/list_role_detail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'authkey': AUTH_KEY,
        },
        body: JSON.stringify({ role_id: roleId }),
      });

      const data = await response.json();
      console.log('📦 Direct fetch role_detail:', data);

      const rows = data.role_detail || [];
      let permissions: ExtendedPermission[];

      if (rows.length === 0) {
        permissions = buildPermissionsFromSingleRow(mapping);
      } else {
        const moduleRightsMap: Record<string, string> = {};
        rows.forEach((row: any) => {
          const menuId = typeof row.module_or_screen === 'string' ? row.module_or_screen.trim() : null;
          if (!menuId) return;
          const existing = moduleRightsMap[menuId] || '';
          const incoming = row.rights || '';
          const merged = Array.from(
            new Set([...existing.split(','), ...incoming.split(',')].filter(Boolean))
          ).join(',');
          moduleRightsMap[menuId] = merged;
        });

        console.log('🗺️ moduleRightsMap:', moduleRightsMap);

        permissions = MENU_ITEMS.map((item) => {
          const rightsStr = moduleRightsMap[item.id] || '';
          return parseRightsToPermission(item.id, item.name, rightsStr);
        });
      }

      setFormData((prev) => ({ ...prev, permissions }));
      setPermissionsLoaded(true);
    } catch (err) {
      console.error('Direct fetch error:', err);
      const permissions = buildPermissionsFromSingleRow(mapping);
      setFormData((prev) => ({ ...prev, permissions }));
      setPermissionsLoaded(true);
    } finally {
      setPermissionsLoading(false);
    }
  }, []);

  const buildPermissionsFromSingleRow = (mapping: RoleMapping): ExtendedPermission[] => {
    let moduleOrScreenObj: Record<string, string> = {};
    if (typeof mapping.module_or_screen === 'object' && mapping.module_or_screen !== null) {
      moduleOrScreenObj = mapping.module_or_screen as Record<string, string>;
    } else if (typeof mapping.module_or_screen === 'string') {
      if (mapping.module_or_screen === 'all') {
        MENU_ITEMS.forEach((item) => { moduleOrScreenObj[item.id] = 'view,add,edit,delete'; });
      } else {
        moduleOrScreenObj[mapping.module_or_screen.trim()] = mapping.rights || 'view';
      }
    }
    return MENU_ITEMS.map((item) => {
      const rightsStr = moduleOrScreenObj[item.id] || '';
      return parseRightsToPermission(item.id, item.name, rightsStr);
    });
  };

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleOpenDialog = (mapping?: RoleMapping) => {
    if (mapping) {
      setEditMode(true);
      setSelectedMapping(mapping);
      setPermissionsLoaded(false);
      setFormData({ role_id: mapping.role_id?.toString() || '', permissions: [] });
      setOpenDialog(true);
      setFormErrors({});
      fetchRoleDetailDirectly(mapping.role_id, mapping);
    } else {
      const initialPermissions: ExtendedPermission[] = MENU_ITEMS.map((item) => ({
        menu_id: item.id,
        menu_name: item.name,
        can_read: false,
        can_write: false,
        can_delete: false,
      }));
      setEditMode(false);
      setSelectedMapping(null);
      setPermissionsLoaded(true);
      setPermissionsLoading(false);
      setFormData({ role_id: '', permissions: initialPermissions });
      setOpenDialog(true);
      setFormErrors({});
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setSelectedMapping(null);
    setPermissionsLoaded(false);
    setPermissionsLoading(false);
    setFormData({ role_id: '', permissions: [] });
    setFormErrors({});
  };

  const handlePermissionChange = (
    menuId: string,
    field: 'can_read' | 'can_write' | 'can_delete',
    value: boolean
  ) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.map((perm) => {
        if (perm.menu_id !== menuId) return perm;

        // If removing read → also remove write & delete
        if (field === 'can_read' && !value) {
          return { ...perm, can_read: false, can_write: false, can_delete: false };
        }
        // If adding write or delete → ensure read is on
        if ((field === 'can_write' || field === 'can_delete') && value) {
          return { ...perm, [field]: value, can_read: true };
        }
        return { ...perm, [field]: value };
      }),
    }));
  };

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};
    if (!formData.role_id) errors.role_id = 'Role is required';

    if (!editMode) {
      const exists = (mappingsData?.role_mappings || []).some(
        (m) => m.role_id?.toString() === formData.role_id
      );
      if (exists) errors.role_id = 'This role already has a mapping. Please edit the existing one.';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const computeAdminType = (permissions: ExtendedPermission[]): 'super_admin' | 'admin' => {
    const allFull = permissions.every((p) => p.can_read && p.can_write && p.can_delete);
    return allFull ? 'super_admin' : 'admin';
  };

  // ── Build module_or_screen payload ─────────────────────────────────────────
  const buildModuleOrScreen = (): Record<string, string> | string => {
    const adminType = computeAdminType(formData.permissions);
    if (adminType === 'super_admin') return 'all';

    const result: Record<string, string> = {};
    formData.permissions.forEach((perm) => {
      if (perm.can_read || perm.can_write || perm.can_delete) {
        result[perm.menu_id] = permissionToRightsString(perm);
      }
    });
    return result;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const moduleOrScreen = buildModuleOrScreen();
    const adminType = computeAdminType(formData.permissions);

    console.log('📤 Submitting module_or_screen:', moduleOrScreen);
    console.log('📤 Computed admin_type:', adminType);

    try {
      if (editMode && selectedMapping) {
        const recid = selectedMapping.recid ?? (selectedMapping as any).recId ?? selectedMapping.id;
        if (!recid) {
          setSnackbar({ open: true, message: 'Cannot update: record ID missing. Please refresh.', severity: 'error' });
          return;
        }

        const result = await updateMapping({
          recid,
          role_id: parseInt(formData.role_id),
          admin_type: adminType,
          module_or_screen: moduleOrScreen,
        }).unwrap();

        if (!result.error) {
          setSnackbar({ open: true, message: result.message || 'Role mapping updated successfully', severity: 'success' });
          refetchMappings();
          setTimeout(handleCloseDialog, 800);
        } else {
          setSnackbar({ open: true, message: result.message || 'Failed to update', severity: 'error' });
        }
      } else {
        const result = await createMapping({
          role_id: parseInt(formData.role_id),
          admin_type: adminType,
          module_or_screen: moduleOrScreen,
        }).unwrap();

        if (!result.error) {
          setSnackbar({ open: true, message: result.message || 'Role mapping created successfully', severity: 'success' });
          refetchMappings();
          setTimeout(handleCloseDialog, 800);
        } else {
          setSnackbar({ open: true, message: result.message || 'Failed to create', severity: 'error' });
        }
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error?.data?.message || error?.error || 'Operation failed', severity: 'error' });
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this role mapping?')) return;
    try {
      const result = await deleteMapping(id).unwrap();
      if (!result.error) {
        setSnackbar({ open: true, message: result.message || 'Deleted successfully', severity: 'success' });
        refetchMappings();
      } else {
        setSnackbar({ open: true, message: result.message || 'Failed to delete', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error?.data?.message || 'Failed to delete', severity: 'error' });
    }
  };

  if (!isSuperAdmin) {
    return (
      <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f1f2f4' }}>
        <Box sx={{ width: '250px', display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
        </Box>
      </Box>
    );
  }

  const displayMappings = mappingsData?.role_mappings || [];
  const loading = isLoadingMappings || isManualRefresh;
  const isSaving = isCreating || isUpdating;

  // Checkbox sx shorthand
  const checkboxSx = { color: '#008060', '&.Mui-checked': { color: '#008060' } };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>

        <Drawer variant="temporary" open={mobileOpen} onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 250 } }}>
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>

        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />

          <Box sx={{ flexGrow: 1, overflow: 'auto', bgcolor: '#f1f2f4' }}>
            <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>

              {/* Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <AssignmentIndIcon sx={{ fontSize: 28, color: '#008060' }} />
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    Role Mapping Management
                  </Typography>
                  <Chip label="Super Admin Only" size="small" color="warning" sx={{ fontWeight: 600 }} />
                  {isMappingsError && <Chip label="API Error" size="small" color="error" sx={{ fontWeight: 600 }} />}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleManualRefresh}
                    disabled={isManualRefresh}
                    sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '8px', fontWeight: 500 }}>
                    {isManualRefresh ? 'Refreshing...' : 'Refresh'}
                  </Button>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}
                    disabled={loading}
                    sx={{ textTransform: 'none', bgcolor: '#008060', fontWeight: 600, borderRadius: '8px', px: 2.5, '&:hover': { bgcolor: '#006e52' } }}>
                    Create Mapping
                  </Button>
                </Box>
              </Box>

              {authError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {authError}
                  <Button size="small" sx={{ ml: 1 }} onClick={() => (window.location.href = '/admin/login')}>Login Again</Button>
                </Alert>
              )}
              {isMappingsError && !authError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Failed to load role mappings.
                  <Button size="small" sx={{ ml: 1 }} onClick={handleManualRefresh}>Retry</Button>
                </Alert>
              )}

              {/* Table */}
              <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                    <CircularProgress size={32} sx={{ color: '#008060' }} />
                    <Typography sx={{ ml: 2 }}>Loading role mappings...</Typography>
                  </Box>
                ) : displayMappings.length > 0 ? (
                  <Box sx={{ bgcolor: 'white', overflowX: 'auto' }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '80px 1fr 130px 200px 120px', gap: 2, px: 3, py: 1.5, borderBottom: '1px solid #e1e3e5', bgcolor: '#fafbfb', minWidth: '800px' }}>
                      {['S.No', 'Role Name', 'Access', 'Created Date', 'Actions'].map((col, i) => (
                        <Typography key={col} variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', textAlign: i === 4 ? 'right' : 'left' }}>
                          {col}
                        </Typography>
                      ))}
                    </Box>

                    {displayMappings.map((mapping, index) => (
                      <Box key={mapping.id} sx={{ display: 'grid', gridTemplateColumns: '80px 1fr 130px 200px 120px', gap: 2, px: 3, py: 2, alignItems: 'center', borderBottom: '1px solid #e1e3e5', '&:hover': { bgcolor: '#f9fafb' }, '&:last-child': { borderBottom: 'none' }, minWidth: '800px' }}>
                        <Typography variant="body2" sx={{ color: '#6d7175', fontWeight: 500 }}>{index + 1}</Typography>
                        <Chip label={mapping.role_name || `Role ${mapping.role_id}`} size="small"
                          sx={{ bgcolor: mapping.admin_type === 'super_admin' ? '#fff3cd' : mapping.admin_type === 'admin' ? '#d1ecf1' : '#d4edda', color: mapping.admin_type === 'super_admin' ? '#856404' : mapping.admin_type === 'admin' ? '#0c5460' : '#155724', fontWeight: 600, fontSize: '12px', width: 'fit-content' }} />
                        <Tooltip title={typeof mapping.module_or_screen === 'object' ? Object.keys(mapping.module_or_screen).join(', ') : mapping.module_or_screen || 'No modules'}>
                          <Chip label={mapping.module_or_screen === 'all' || mapping.admin_type === 'super_admin' ? 'Full Access' : 'Custom'} size="small"
                            sx={{ bgcolor: mapping.module_or_screen === 'all' || mapping.admin_type === 'super_admin' ? '#e8f5e9' : '#e3f2fd', color: mapping.module_or_screen === 'all' || mapping.admin_type === 'super_admin' ? '#2e7d32' : '#1565c0', fontWeight: 500, fontSize: '11px', width: 'fit-content' }} />
                        </Tooltip>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                          {formatDate(mapping.createddt || mapping.created_at || mapping.createdAt || mapping.createdDt || mapping.created)}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <IconButton size="small" onClick={() => handleOpenDialog(mapping)} disabled={isDeleting} sx={{ color: '#6d7175', '&:hover': { bgcolor: '#f1f2f4' } }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDelete(mapping.id)} disabled={isDeleting} sx={{ color: '#d32f2f', '&:hover': { bgcolor: '#ffebee' } }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                    <AssignmentIndIcon sx={{ fontSize: 64, color: '#c4cdd5', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>No role mappings found</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {isMappingsError ? 'Unable to load data.' : 'Click "Create Mapping" to get started.'}
                    </Typography>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}
                      sx={{ textTransform: 'none', bgcolor: '#008060', fontWeight: 600, borderRadius: '8px', px: 2.5, '&:hover': { bgcolor: '#006e52' } }}>
                      Create First Mapping
                    </Button>
                  </Box>
                )}
              </Card>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ─── Dialog ─── */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : '16px', maxHeight: '90vh' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e1e3e5', fontWeight: 600 }}>
          {editMode ? 'Edit Role Mapping' : 'Create Role Mapping'}
          <IconButton size="small" onClick={handleCloseDialog} disabled={isSaving}><CloseIcon /></IconButton>
        </DialogTitle>

        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            <Stack spacing={3}>

              {/* Role selector */}
              <FormControl fullWidth error={!!formErrors.role_id} required>
                <InputLabel>Select Role</InputLabel>
                <Select value={formData.role_id}
                  onChange={(e: SelectChangeEvent) => {
                    setFormData((prev) => ({ ...prev, role_id: e.target.value }));
                    setFormErrors((prev) => ({ ...prev, role_id: '' }));
                  }}
                  label="Select Role"
                  disabled={isSaving || isLoadingRoles || editMode}>
                  {isLoadingRoles
                    ? <MenuItem disabled>Loading roles...</MenuItem>
                    : rolesData?.roles?.length
                      ? rolesData.roles.map((role: any) => (
                          <MenuItem key={role.id} value={role.id.toString()}>{role.role_name}</MenuItem>
                        ))
                      : <MenuItem disabled>No roles available</MenuItem>}
                </Select>
                {formErrors.role_id && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>{formErrors.role_id}</Typography>}
                {editMode && <Typography variant="caption" sx={{ mt: 0.5, ml: 1.5, color: '#6d7175' }}>Role cannot be changed during edit</Typography>}
              </FormControl>

              {/* ── Permissions Table ── */}
              {permissionsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                  <CircularProgress size={28} sx={{ color: '#008060' }} />
                  <Typography sx={{ ml: 2, color: '#6d7175', fontSize: '14px' }}>Loading permissions from database...</Typography>
                </Box>
              ) : formData.permissions.length > 0 ? (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#202223' }}>Menu Permissions Configuration</Typography>
                    {editMode && <Chip label="Loaded from DB" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontSize: '10px' }} />}
                  </Box>

                  <TableContainer component={Paper} sx={{ maxHeight: 420, border: '1px solid #e1e3e5', borderRadius: '8px', overflow: 'auto' }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ bgcolor: '#fafbfb', fontWeight: 700, fontSize: '12px', width: '34%', color: '#202223' }}>
                            Menu Name
                          </TableCell>
                          <TableCell align="center" sx={{ bgcolor: '#fafbfb', fontWeight: 700, fontSize: '12px', width: '22%', color: '#1565c0' }}>
                            Read Access
                            <Typography variant="caption" display="block" sx={{ color: '#6d7175', fontWeight: 400 }}>view</Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ bgcolor: '#fafbfb', fontWeight: 700, fontSize: '12px', width: '22%', color: '#2e7d32' }}>
                            Write Access
                            <Typography variant="caption" display="block" sx={{ color: '#6d7175', fontWeight: 400 }}>add, edit</Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ bgcolor: '#fafbfb', fontWeight: 700, fontSize: '12px', width: '22%', color: '#c62828' }}>
                            Delete Access
                            <Typography variant="caption" display="block" sx={{ color: '#6d7175', fontWeight: 400 }}>delete</Typography>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {formData.permissions.map((perm) => (
                          <TableRow key={perm.menu_id} sx={{ '&:hover': { bgcolor: '#f9fafb' }, '&:last-child td': { border: 0 } }}>
                            <TableCell sx={{ fontSize: '13px', fontWeight: 500 }}>{perm.menu_name}</TableCell>

                            {/* Read */}
                            <TableCell align="center">
                              <Checkbox
                                checked={perm.can_read}
                                onChange={(e) => handlePermissionChange(perm.menu_id, 'can_read', e.target.checked)}
                                disabled={isSaving}
                                sx={{ color: '#1565c0', '&.Mui-checked': { color: '#1565c0' } }}
                              />
                            </TableCell>

                            {/* Write (add + edit) */}
                            <TableCell align="center">
                              <Tooltip title={!perm.can_read ? 'Enable Read first' : ''}>
                                <span>
                                  <Checkbox
                                    checked={perm.can_write}
                                    onChange={(e) => handlePermissionChange(perm.menu_id, 'can_write', e.target.checked)}
                                    disabled={!perm.can_read || isSaving}
                                    sx={{ color: '#2e7d32', '&.Mui-checked': { color: '#2e7d32' } }}
                                  />
                                </span>
                              </Tooltip>
                            </TableCell>

                            {/* Delete */}
                            <TableCell align="center">
                              <Tooltip title={!perm.can_read ? 'Enable Read first' : ''}>
                                <span>
                                  <Checkbox
                                    checked={perm.can_delete}
                                    onChange={(e) => handlePermissionChange(perm.menu_id, 'can_delete', e.target.checked)}
                                    disabled={!perm.can_read || isSaving}
                                    sx={{ color: '#c62828', '&.Mui-checked': { color: '#c62828' } }}
                                  />
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Legend */}
                  <Box sx={{ mt: 1.5, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="caption" sx={{ color: '#6d7175' }}>
                      💡 <strong>Read</strong> = view &nbsp;|&nbsp; <strong>Write</strong> = add, edit &nbsp;|&nbsp; <strong>Delete</strong> = delete
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6d7175' }}>
                      Write &amp; Delete require Read to be enabled first.
                    </Typography>
                  </Box>
                </Box>
              ) : null}

            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 3, pt: 2, borderTop: '1px solid #e1e3e5' }}>
            <Button onClick={handleCloseDialog} disabled={isSaving}
              sx={{ textTransform: 'none', fontWeight: 600, color: '#eeeeee', bgcolor: '#f44336', borderRadius: '8px' }}>
              Close
            </Button>
            <Button onClick={handleCloseDialog} disabled={isSaving}
              sx={{ textTransform: 'none', fontWeight: 600, color: '#eeeeee', bgcolor: '#F78E12', borderRadius: '8px' }}>
              Cancel
            </Button>
            <Button type="submit" variant="contained"
              disabled={isSaving || permissionsLoading || (editMode && !permissionsLoaded)}
              sx={{ textTransform: 'none', bgcolor: '#008060', fontWeight: 600, borderRadius: '8px', px: 2.5, '&:hover': { bgcolor: '#006e52' } }}>
              {isSaving
                ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} color="inherit" />
                    {editMode ? 'Updating...' : 'Creating...'}
                  </Box>
                : editMode ? 'Update Mapping' : 'Create Mapping'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RoleMappingManagement;